import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { buildBaseDomainUrl } from "@shared/utils/subdomain-url.utils";
import { UserAppRoutes } from "@shared/constants/user-app-routes.constants";
import { EventIdPublic } from "@shared/schemas/branded-id.schemas";
import { useUserActions } from "@frontend/shared/features/actions/application/actions/use-user-actions";
import { useEventSubdomain } from "@/shared/presentation/hooks/useEventSubdomain";
import type { MyEventViewUnion } from "@shared/schemas/event-views.schemas";

interface YourEventsTabControllerReturn {
  readonly state: {
    readonly isLoading: boolean;
    readonly upcoming: readonly MyEventViewUnion[];
    readonly past: readonly MyEventViewUnion[];
  };
  readonly actions: {
    readonly onEventClick: (eventIdPublic: string) => void;
  };
}

export function useYourEventsTabController(): YourEventsTabControllerReturn {
  const navigate = useNavigate();
  const { useGetUserEventsAsParticipant } = useUserActions();
  const { data, isLoading } = useGetUserEventsAsParticipant();
  const eventSubdomain = useEventSubdomain();

  const [mountTime] = useState(() => Date.now());
  const { upcoming, past } = useMemo(() => {
    const events = data ?? [];
    const nowMs = mountTime;
    const upcomingList: MyEventViewUnion[] = [];
    const pastList: MyEventViewUnion[] = [];
    for (const e of events) {
      (e.endAt.getTime() > nowMs ? upcomingList : pastList).push(e);
    }
    return { upcoming: upcomingList, past: pastList };
  }, [data, mountTime]);

  const onEventClick = (eventIdPublic: string): void => {
    const canonicalPath = UserAppRoutes.forEvent({
      eventIdPublic: EventIdPublic.unsafeCreate(eventIdPublic),
      isEventSubdomain: false,
    }).directory();
    if (eventSubdomain !== null) {
      const baseDomainUrl = buildBaseDomainUrl(window.location.host);
      if (baseDomainUrl !== null) {
        window.location.href = `${baseDomainUrl}${canonicalPath}`;
        return;
      }
    }
    void navigate(canonicalPath);
  };

  return {
    state: { isLoading, upcoming, past },
    actions: { onEventClick },
  };
}
